// <flock-gauge> — a Flock gauge rendered in Blender, with a live screen, LED ring and glass shine.
//
//   <script type="module" src="flock-gauge.js"></script>
//   <flock-gauge view="hero" face="z31" demo="warmup"></flock-gauge>
//
// Attributes
//   view        hero | up | front | right      which Blender camera plate to use
//   mode        analog | digital                analog face, or the digital four-sensor display
//   face        face id from faces/faces.json   analog face and needle on the screen
//   warning     face id                        background swapped in while the reading is high
//   high        water temp in °F               warning threshold (default 220)
//   max         water temp in °F               caps the analog reading
//   cycle       comma-separated steps          swap every 2 seconds; "digital" and "eggy" show 1–4 sensors
//   value       water temp in °F                sets the analog reading (stops any demo)
//   demo        warmup | warning | sweep | quad loop a scripted reading; quad is the digital display
//   led-gain    LED brightness multiplier (default 0.85)
//   assets      base URL for assets/ and faces/ (defaults to this script's folder)
//   flat        present = asset paths flattened to "flock-gauge-<dir>-<file>" (Shopify assets/)
//
// The screen and LEDs follow the firmware: the needle turns percent * 240° about the image center,
// and the ring lights round(percent * 240 / 360 * 24) LEDs from LED 0, green, red when "high".

const NUM_LEDS = 24;
const NEEDLE_SWEEP_DEG = 240;
const TEMP_FULL_SCALE_F = 302;
const WATER_HIGH_F = 220;
const ANALOG_NORMAL = [0, 1, 0];
const ANALOG_WARNING = [1, 0, 0];
const SCREEN_PX = 480;
const SCREEN_SRC = 240;
// Rasterize the outline font above the screen size, then shrink it onto the plate.
const DIGITAL_SUPERSAMPLE = 4;
const RIFT_FAMILY = "Flock Rift";
const BLENNY_FAMILY = "Flock Blenny";
const DIGITAL_BLUE = [0x33 / 255, 0x7f / 255, 0xd4 / 255];
const QUAD_LAYOUT = [
  { label: "WATER TEMP", x: 69, y: 43, labelX: 63, labelY: 95, size: 62 },
  { label: "RPM", x: 171, y: 43, labelX: 165, labelY: 95, size: 62 },
  { label: "SPEED", x: 69, y: 122, labelX: 63, labelY: 175, size: 62 },
  { label: "OIL TEMP", x: 171, y: 122, labelX: 165, labelY: 175, size: 62 },
];
// Firmware positions and point sizes. One sensor is placed by ink top: the value at 100, then the Rift label under it.
const DIGITAL_LAYOUTS = {
  1: [{ label: "WATER TEMP" }],
  2: [
    { label: "WATER TEMP", x: 120, y: 29, labelX: 120, labelY: 103, size: 92 },
    { label: "RPM", x: 120, y: 136, labelX: 120, labelY: 210, size: 92 },
  ],
  3: [
    { label: "WATER TEMP", x: 120, y: 33, labelX: 120, labelY: 108, size: 92 },
    { label: "RPM", x: 67, y: 128, labelX: 67, labelY: 181, size: 62 },
    { label: "SPEED", x: 173, y: 128, labelX: 173, labelY: 181, size: 62 },
  ],
  4: QUAD_LAYOUT,
};
const SENSOR_LABELS = [...QUAD_LAYOUT.map((slot) => slot.label), "OIL PRESSURE", "BOOST", "FUEL PRESSURE"];
const RPM_IDLE = 800;
const RPM_MAX = 7500;
const SPEED_MAX = 60;
const DRIVE_LOOP = 18;
const SPRING_HZ = 1.8;
const SPRING_DAMPING = 0.62;

const DEMOS = {
  // Cold engine, then the reading climbs and holds at operating temperature.
  warmup: { keys: [[0, 70], [0.8, 70], [3.2, 186], [3.9, 181]], idle: 181, wobble: 1.2 },
  // Crosses the warning threshold, holds at 205°F, then cools back down.
  warning: { keys: [[0, 184], [1.5, 190], [3.6, 205], [6.5, 205], [8.2, 190], [9.5, 184]], loop: 9.5 },
  // Full-scale showcase sweep.
  sweep: { keys: [[0, 0], [2.2, 302], [3.2, 302], [5.4, 0], [6.2, 0]], loop: 6.2 },
};

const FACE_CYCLE_MS = 2000;
const EGGY_FRAME_MS = 100;
const EGGY_FRAME_W = 50;
const EGGY_FRAME_H = 48;
const EGGY_FRAME_COUNT = 8;
const EGGY_SPRITE_Y = 160;
const EGGY_LED = { r: 0, g: 132, b: 240 };
// Single-sensor screen. Eggygotchi draws the value in Blenny; the digital face uses Rift.
const SINGLE_VALUE_TOP = 82;
const SINGLE_VALUE_SIZE = 100;
const SINGLE_LABEL_TOP = 176;
const SINGLE_LABEL_SIZE = 18;
const SCRIPT_BASE = new URL(".", import.meta.url).href;

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

function curve(keys, t) {
  if (t <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) {
    const [t1, v1] = keys[i];
    const [t0, v0] = keys[i - 1];
    if (t <= t1) return v0 + (v1 - v0) * smoothstep((t - t0) / (t1 - t0));
  }
  return keys[keys.length - 1][1];
}

function wobble(t) {
  return (Math.sin(t * 2.1) + Math.sin(t * 3.7 + 1.3) + Math.sin(t * 0.9 + 2.2) + Math.sin(t * 5.3 + 0.4)) / 4;
}

export function waterTempSensor(tempF, highF = WATER_HIGH_F) {
  const percent = Math.min(1, Math.max(0, tempF / TEMP_FULL_SCALE_F));
  return { percent, state: tempF >= highF ? "high" : "normal" };
}

export function analogLeds(percent, state) {
  const n = Math.min(NUM_LEDS, Math.max(0, Math.round((Math.abs(percent * NEEDLE_SWEEP_DEG) / 360) * NUM_LEDS)));
  const color = state === "high" ? ANALOG_WARNING : ANALOG_NORMAL;
  return Array.from({ length: NUM_LEDS }, (_, i) => (i < n ? color : null));
}

export function digitalQuadLeds() {
  return Array.from({ length: NUM_LEDS }, () => DIGITAL_BLUE);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function channelLuminance(value) {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function rgbLuminance(r, g, b) {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

function contrastRatio(lighter, darker) {
  return (Math.max(lighter, darker) + 0.05) / (Math.min(lighter, darker) + 0.05);
}

function hslToRgb(hue, saturation, lightness) {
  const chroma = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n) => {
    const k = (n + hue / 30) % 12;
    return lightness - chroma * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [channel(0), channel(8), channel(4)].map((value) => Math.round(value * 255));
}

// A saturated color dark enough that white type stays readable after the screen brightness filter.
function randomContrastColor(level, previousHue) {
  const text = Math.round(255 * (level || 0.82));
  const textLuminance = rgbLuminance(text, text, text);
  let hue = Math.random() * 360;
  for (let attempt = 0; previousHue !== undefined && hueDistance(hue, previousHue) < 48 && attempt < 8; attempt += 1) {
    hue = Math.random() * 360;
  }
  const saturation = 0.68 + Math.random() * 0.32;
  let lightness = 0.42;
  let [r, g, b] = hslToRgb(hue, saturation, lightness);
  while (contrastRatio(textLuminance, rgbLuminance(r, g, b)) < 4.5 && lightness > 0.08) {
    lightness -= 0.015;
    [r, g, b] = hslToRgb(hue, saturation, lightness);
  }
  return { r, g, b, hue };
}

function hueDistance(a, b) {
  const delta = Math.abs(a - b) % 360;
  return Math.min(delta, 360 - delta);
}

function quadReadings(t) {
  const time = t % DRIVE_LOOP;
  let rpm = RPM_IDLE;
  let speed = 0;

  if (time < 4.6) {
    // Two revs in neutral. The car stays stopped.
    rpm = curve([[0, 800], [0.6, 850], [1.45, 5200], [2.15, 1300], [3.3, 6800], [4.15, 6400], [4.6, 6100]], time);
  } else if (time < 5.15) {
    // The clutch drops and the engine loads before the tires break loose.
    rpm = 6100 + (3900 - 6100) * smoothstep((time - 4.6) / 0.55);
  } else if (time < 8.1) {
    // Burnout: the revs flare and chatter while the car only creeps.
    const burn = (time - 5.15) / 2.95;
    rpm = 6800 + Math.sin(time * 16) * 420 + wobble(time) * 160;
    speed = 2 + burn * 4 + Math.sin(time * 8) * 1.1;
  } else if (time < 9.05) {
    // The burnout ends back at a stop.
    const settle = smoothstep((time - 8.1) / 0.95);
    rpm = 7000 + (RPM_IDLE - 7000) * settle;
    speed = 6 * (1 - settle);
  } else if (time < 14.4) {
    // Zero to sixty. Speed follows RPM from idle to redline.
    const load = curve([[9.05, 0], [9.7, 0.12], [11.6, 0.58], [12.35, 0.5], [14.4, 1]], time);
    rpm = RPM_IDLE + load * (RPM_MAX - RPM_IDLE) + wobble(time) * 70;
    speed = load * SPEED_MAX;
  } else {
    const ease = smoothstep(Math.min(1, (time - 14.4) / (DRIVE_LOOP - 14.4)));
    rpm = RPM_MAX + (RPM_IDLE - RPM_MAX) * ease;
    speed = SPEED_MAX * (1 - ease);
  }

  const oilHeat = time > 4.6 && time < 9.05 ? 2.5 : 0;
  const oil = Math.round(195 + Math.sin(time * 0.4) * 2.2 + oilHeat);
  const water = Math.round(181 + wobble(time) * 0.35);
  const oilPressure = Math.round(24 + clamp((rpm - RPM_IDLE) / (RPM_MAX - RPM_IDLE), 0, 1) * 42);
  const boost = (clamp((rpm - 3000) / (RPM_MAX - 3000), 0, 1) * 14).toFixed(1);
  const fuelPressure = Math.round(43 + Number(boost));
  return [`${water}°`, String(Math.round(clamp(rpm, RPM_IDLE, RPM_MAX))), String(Math.round(clamp(speed, 0, SPEED_MAX))), `${oil}°`, String(oilPressure), boost, String(fuelPressure)];
}

let riftFontPromise = null;

function ensureRiftFont(url) {
  if (!riftFontPromise) {
    const face = new FontFace(RIFT_FAMILY, `url(${url})`);
    riftFontPromise = face.load().then((loaded) => {
      document.fonts.add(loaded);
      return loaded;
    });
  }
  return riftFontPromise;
}

let blennyFontPromise = null;

function ensureBlennyFont(url) {
  if (!blennyFontPromise) {
    const face = new FontFace(BLENNY_FAMILY, `url(${url})`);
    blennyFontPromise = face.load().then((loaded) => {
      document.fonts.add(loaded);
      return loaded;
    });
  }
  return blennyFontPromise;
}

function drawInkTop(ctx, text, x, inkTop, size, family, color = "#fff") {
  ctx.font = `${size}px "${family}"`;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  const metrics = ctx.measureText(text);
  ctx.fillText(text, x, inkTop + metrics.actualBoundingBoxAscent);
}

// Rift OS/2 sTypoAscender / unitsPerEm. Chromium's textBaseline "top" uses this.
// WebKit (iPhone Chrome) uses the hhea ascender, 0.98em, and the readings land on the labels.
const RIFT_TYPO_ASCENT = 0.78;

function drawOutline(ctx, text, x, y, size, color = "#fff") {
  ctx.font = `${size}px "${RIFT_FAMILY}"`;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, x, y + size * RIFT_TYPO_ASCENT);
}

function eggyPose(origin, start, now) {
  let frame = origin.frame;
  let x = origin.x;
  let right = origin.right;
  const steps = Math.max(0, Math.floor((now - start) / EGGY_FRAME_MS));
  for (let i = 0; i < steps; i += 1) {
    frame = (frame + 1) % EGGY_FRAME_COUNT;
    if (right) {
      x += 2;
      if (x >= SCREEN_SRC - EGGY_FRAME_W) right = false;
    } else {
      x -= 2;
      if (x <= 0) right = true;
    }
  }
  return { frame, x, right };
}

// Frames are cut at the sheet's native resolution and drawn at EGGY_FRAME_W x EGGY_FRAME_H screen units.
function cutEggyFrames(sheet) {
  const sheetW = sheet.naturalWidth || sheet.width;
  const frameW = Math.round(sheetW / EGGY_FRAME_COUNT);
  const frameH = sheet.naturalHeight || sheet.height;
  const frames = [];
  for (const flip of [false, true]) {
    for (let frame = 0; frame < EGGY_FRAME_COUNT; frame += 1) {
      const canvas = document.createElement("canvas");
      canvas.width = frameW;
      canvas.height = frameH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      if (flip) ctx.setTransform(-1, 0, 0, 1, frameW, 0);
      ctx.drawImage(sheet, frame * frameW, 0, frameW, frameH, 0, 0, frameW, frameH);
      frames.push(canvas);
    }
  }
  return frames;
}

// Unit square -> quad as a CSS matrix3d for an element of size s (Heckbert's projective mapping).
function quadMatrix(s, [x0, y0], [x1, y1], [x2, y2], [x3, y3]) {
  const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
  const den = dx1 * dy2 - dx2 * dy1;
  const g = (dx3 * dy2 - dx2 * dy3) / den;
  const h = (dx1 * dy3 - dx3 * dy1) / den;
  const a = x1 - x0 + g * x1, b = x3 - x0 + h * x3;
  const d = y1 - y0 + g * y1, e = y3 - y0 + h * y3;
  const m = [a / s, d / s, 0, g / s, b / s, e / s, 0, h / s, 0, 0, 1, 0, x0, y0, 0, 1];
  return `matrix3d(${m.map((v) => +v.toFixed(9)).join(",")})`;
}

const imageCache = new Map();
const faceCache = new Map();
let facesListPromise = null;

function loadImage(url) {
  const cached = imageCache.get(url);
  if (cached) return cached;
  const pending = new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (img.decode) img.decode().then(() => resolve(img), () => resolve(img));
      else resolve(img);
    };
    img.onerror = () => reject(new Error("flock-gauge: failed to load " + url));
    img.src = url;
  });
  imageCache.set(url, pending);
  return pending;
}

function facesList(asset) {
  const url = asset("faces/faces.json");
  if (!facesListPromise) facesListPromise = fetch(url).then((response) => response.json());
  return facesListPromise;
}

function faceCacheKey(id, warning) {
  return `${id}|${warning || ""}`;
}

const STYLE = `
  :host { display: block; position: relative; aspect-ratio: 1; overflow: hidden; contain: paint; }
  .stage { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
  .stage > * { position: absolute; left: 0; top: 0; }
  .screen { overflow: hidden; -webkit-mask-size: 100% 100%; mask-size: 100% 100%; }
  .screen canvas { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
  .leds, .shine { mix-blend-mode: screen; pointer-events: none; }
  @supports (mix-blend-mode: plus-lighter) { .leds, .shine { mix-blend-mode: plus-lighter; } }
  img { user-select: none; -webkit-user-drag: none; }
`;

class FlockGauge extends HTMLElement {
  static observedAttributes = ["view", "face", "warning", "value", "demo", "mode", "cycle"];

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    this.shadow.innerHTML = `<style>${STYLE}</style><div class="stage">
      <img class="base" alt="">
      <div class="screen"><canvas width="${SCREEN_PX}" height="${SCREEN_PX}"></canvas></div>
      <canvas class="leds"></canvas>
      <img class="shine" alt="">
    </div>`;
    const $ = (s) => this.shadow.querySelector(s);
    this.el = { stage: $(".stage"), base: $(".base"), screen: $(".screen"), face: $(".screen canvas"),
      leds: $(".leds"), shine: $(".shine") };
    this.faceCtx = this.el.face.getContext("2d");
    this.ledCtx = this.el.leds.getContext("2d");
    this.digital = document.createElement("canvas");
    this.digital.width = SCREEN_SRC * DIGITAL_SUPERSAMPLE;
    this.digital.height = SCREEN_SRC * DIGITAL_SUPERSAMPLE;
    this.digitalCtx = this.digital.getContext("2d");
    this.temp = 70;
    this.velocity = 0;
    this.target = 70;
    this.ledKey = "";
    this.faceKey = "";
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.resize = new ResizeObserver(() => this.fit());
    this.resize.observe(this);
    this.loadView();
    this.loadContent();
    this.startDemo();
    this.startFaceCycle();
    this.raf = requestAnimationFrame(this.tick);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.stopFaceCycle();
    this.resize?.disconnect();
  }

  attributeChangedCallback(name, oldValue, value) {
    if (!this.isConnected || oldValue === value) return;
    if (name === "view") this.loadView();
    if ((name === "face" || name === "warning" || name === "mode") && !this.holdingContent) this.loadContent();
    if (name === "demo") this.startDemo();
    if (name === "cycle") this.startFaceCycle();
    if (name === "value" && value !== null) {
      this.demo = null;
      this.target = parseFloat(value);
    }
  }

  get value() {
    return this.target;
  }

  set value(tempF) {
    this.setAttribute("value", String(tempF));
  }

  asset(path) {
    const base = this.getAttribute("assets") || SCRIPT_BASE;
    const file = this.hasAttribute("flat") ? "flock-gauge-" + path.replaceAll("/", "-") : path;
    return new URL(file, base).href;
  }

  async loadView() {
    const view = this.getAttribute("view") || "hero";
    const token = (this.viewToken = {});
    const plate = await (await fetch(this.asset(`assets/${view}/plate.json`))).json();
    const [base, shine, atlas] = await Promise.all([
      loadImage(this.asset(`assets/${view}/base.webp`)),
      loadImage(this.asset(`assets/${view}/shine.webp`)),
      loadImage(this.asset(`assets/${view}/leds.webp`)),
    ]);
    if (token !== this.viewToken) return;

    const { width, height, screen } = plate;
    const { el } = this;
    this.style.aspectRatio = `${width} / ${height}`;
    el.stage.style.width = el.base.style.width = width + "px";
    el.stage.style.height = el.base.style.height = height + "px";
    el.base.src = base.src;
    const m = screen.mask;
    const place = (node) => Object.assign(node.style, { left: m.x + "px", top: m.y + "px", width: m.w + "px", height: m.h + "px" });
    place(el.screen);
    place(el.shine);
    el.shine.src = shine.src;
    const maskUrl = `url("${this.asset(`assets/${view}/screen_mask.png`)}")`;
    el.screen.style.webkitMaskImage = el.screen.style.maskImage = maskUrl;
    const c = screen.corners;
    const rel = ([x, y]) => [x - m.x, y - m.y];
    el.face.style.transform = quadMatrix(SCREEN_PX, rel(c.tl), rel(c.tr), rel(c.br), rel(c.bl));
    el.face.style.filter = `brightness(${screen.level})`;
    el.leds.width = width;
    el.leds.height = height;

    this.plate = plate;
    this.atlas = atlas;
    this.ledKey = "";
    this.fit();
    this.draw();
  }

  get digitalMode() {
    return this.getAttribute("mode") === "digital";
  }

  loadContent() {
    if (this.digitalMode) this.loadFont();
    else this.loadFace();
  }

  async loadFont() {
    const token = (this.fontToken = {});
    await ensureRiftFont(this.asset("font/rift-bold-italic.otf"));
    if (token !== this.fontToken) return;
    this.font = true;
    this.faceKey = "";
    this.draw();
  }

  startFaceCycle() {
    this.stopFaceCycle();
    const faces = (this.getAttribute("cycle") || "").split(",").map((id) => id.trim()).filter(Boolean);
    if (faces.length < 2) return;
    for (const id of faces) {
      if (id === "digital" || id === "eggy") continue;
      this.preloadFace(id).catch(() => {});
    }
    if (faces.includes("digital") || faces.includes("eggy")) ensureRiftFont(this.asset("font/rift-bold-italic.otf")).catch(() => {});
    if (faces.includes("eggy")) {
      this.loadEggy().catch(() => {});
      ensureBlennyFont(this.asset("font/blenny-black.ttf")).then(() => { this.blennyReady = true; }).catch(() => {});
    }
    const current = this.eggy ? "eggy" : this.getAttribute("mode") === "digital" ? "digital" : this.getAttribute("face") || faces[0];
    let index = Math.max(0, faces.indexOf(current));
    this.faceCycle = window.setInterval(() => {
      index = (index + 1) % faces.length;
      this.showCycleStep(faces[index]);
    }, FACE_CYCLE_MS);
  }

  showCycleStep(step) {
    this.cycleToken = step;
    if (step === "digital" || step === "eggy") {
      this.showScreenStep(step === "eggy");
      return;
    }
    const ready = faceCache.get(faceCacheKey(step, this.getAttribute("warning")));
    if (ready && typeof ready.then !== "function") {
      this.applyAnalogFace(step, ready);
      return;
    }
    this.preloadFace(step).then((assets) => {
      if (this.cycleToken !== step) return;
      this.applyAnalogFace(step, assets);
    }).catch(() => {});
  }

  showScreenStep(eggy) {
    const token = eggy ? "eggy" : "digital";
    const apply = () => {
      if (this.cycleToken !== token) return;
      this.captureEggyPose();
      this.eggy = eggy;
      this.faceKey = "";
      this.ledKey = "";
      this.activeSensors = this.pickSensorCount();
      if (!this.analogDemo) this.analogDemo = this.demo;
      this.demo = { quad: true, start: performance.now() };
      if (eggy) {
        this.screenColor = { ...EGGY_LED, hue: this.screenHue };
        this.eggyVisitStart = performance.now();
        this.eggyVisitOrigin = {
          frame: this.eggyFrame || 0,
          x: this.eggyX || 0,
          right: this.eggyRight !== false,
        };
      } else {
        const level = this.plate?.screen?.level || 0.82;
        this.screenColor = randomContrastColor(level, this.screenHue);
        this.screenHue = this.screenColor.hue;
      }
      if (this.getAttribute("mode") !== "digital") this.setAttribute("mode", "digital");
      else this.draw();
    };
    if (eggy && (!this.eggyFrames || !this.blennyReady)) {
      Promise.all([
        this.loadEggy(),
        ensureBlennyFont(this.asset("font/blenny-black.ttf")),
      ]).then(() => {
        this.blennyReady = true;
        apply();
      }).catch(() => {});
      return;
    }
    apply();
  }

  async setEggyDisplay(enabled) {
    const token = this.eggyDisplayToken = {};
    this.captureEggyPose();
    this.eggy = false;
    this.faceKey = "";
    if (!enabled) return;
    await Promise.all([
      this.loadEggy(),
      ensureBlennyFont(this.asset("font/blenny-black.ttf")),
    ]);
    if (this.eggyDisplayToken !== token) return;
    this.blennyReady = true;
    this.eggy = true;
    this.eggyVisitStart = performance.now();
    this.eggyVisitOrigin = {
      frame: this.eggyFrame || 0,
      x: this.eggyX || 0,
      right: this.eggyRight !== false,
    };
    this.faceKey = "";
    this.draw();
  }

  captureEggyPose() {
    if (!this.eggy || !this.eggyVisitStart || !this.eggyVisitOrigin) return;
    const pose = eggyPose(this.eggyVisitOrigin, this.eggyVisitStart, performance.now());
    this.eggyFrame = pose.frame;
    this.eggyX = pose.x;
    this.eggyRight = pose.right;
  }

  async loadEggy() {
    if (this.eggyLoad) return this.eggyLoad;
    this.eggyLoad = Promise.all([
      loadImage(this.asset("eggygotchi/background.jpg")),
      loadImage(this.asset("eggygotchi/eggsprite.png")),
    ]).then(([background, sheet]) => {
      this.eggyBg = background;
      this.eggyFrames = cutEggyFrames(sheet);
    });
    return this.eggyLoad;
  }

  applyAnalogFace(step, assets) {
    this.captureEggyPose();
    this.eggy = false;
    this.face = assets;
    this.faceKey = "";
    this.ledKey = "";
    if (this.analogDemo) this.demo = this.analogDemo;
    this.holdingContent = true;
    if (this.getAttribute("mode") !== "analog") this.setAttribute("mode", "analog");
    if (this.getAttribute("face") !== assets.id) this.setAttribute("face", assets.id);
    this.holdingContent = false;
    this.draw();
  }

  preloadFace(id) {
    const warning = this.getAttribute("warning") || "";
    const key = faceCacheKey(id, warning);
    const existing = faceCache.get(key);
    if (existing) return Promise.resolve(existing);
    const pending = this.loadFaceAssets(id, warning).then((assets) => {
      faceCache.set(key, assets);
      return assets;
    });
    faceCache.set(key, pending);
    return pending;
  }

  async loadFaceAssets(id, warning) {
    const list = await facesList((path) => this.asset(path));
    const info = list.find((face) => face.id === id) || list.find((face) => face.id === "z31");
    const warningId = warning || (info.warning ? info.id : "");
    const [face, needle, warningImage] = await Promise.all([
      loadImage(this.asset(`faces/${info.id}/face.webp`)),
      loadImage(this.asset(`faces/${info.id}/needle.png`)),
      warningId ? loadImage(this.asset(`faces/${warningId}/warning.webp`)) : null,
    ]);
    return { id: info.id, face, needle, warning: warningImage };
  }

  pickSensorCount() {
    let count = 1 + Math.floor(Math.random() * 4);
    if (count === this.activeSensors) count = (count % 4) + 1;
    return count;
  }

  stopFaceCycle() {
    clearInterval(this.faceCycle);
    this.faceCycle = 0;
  }

  async loadFace() {
    const id = this.getAttribute("face") || "z31";
    const token = (this.faceToken = {});
    const assets = await this.preloadFace(id);
    if (token !== this.faceToken) return;
    this.face = assets;
    this.faceKey = "";
    this.draw();
  }

  startDemo() {
    const name = this.getAttribute("demo");
    if (name === "quad") {
      this.demo = { quad: true, start: performance.now() };
      return;
    }
    this.demo = DEMOS[name] ? { ...DEMOS[name], start: performance.now() } : null;
    if (!this.demo && this.hasAttribute("value")) this.target = parseFloat(this.getAttribute("value"));
  }

  fit() {
    if (!this.plate) return;
    this.el.stage.style.transform = `scale(${this.clientWidth / this.plate.width})`;
  }

  tick(now) {
    // Throttled or backgrounded tabs deliver sparse frames; integrate the gap instead of lagging.
    const dt = Math.min(1, this.last ? (now - this.last) / 1000 : 0);
    this.last = now;
    if (this.demo?.keys) {
      const d = this.demo;
      let t = (now - d.start) / 1000;
      const end = d.keys[d.keys.length - 1][0];
      if (d.loop) t %= d.loop;
      this.target = t > end && d.idle !== undefined ? d.idle + d.wobble * wobble(t) : curve(d.keys, t);
    }
    // Critically-ish damped spring so the needle eases like a real sensor feed.
    const w = 2 * Math.PI * SPRING_HZ;
    const steps = Math.max(1, Math.ceil(dt / 0.004));
    for (let i = 0; i < steps; i++) {
      const h = dt / steps;
      this.velocity += (w * w * (this.target - this.temp) - 2 * SPRING_DAMPING * w * this.velocity) * h;
      this.temp += this.velocity * h;
    }
    const cap = parseFloat(this.getAttribute("max"));
    if (Number.isFinite(cap)) {
      if (this.target > cap) this.target = cap;
      if (this.temp > cap) {
        this.temp = cap;
        if (this.velocity > 0) this.velocity = 0;
      }
    }
    this.draw();
    this.raf = requestAnimationFrame(this.tick);
  }

  draw() {
    if (!this.plate) return;
    if (this.digitalMode) {
      if (!this.font) return;
      this.drawDigital();
      this.drawLeds(this.digitalLeds());
      return;
    }
    if (!this.face) return;
    const high = parseFloat(this.getAttribute("high") || WATER_HIGH_F);
    const { percent, state } = waterTempSensor(this.temp, high);
    this.drawScreen(percent, state);
    this.drawLeds(this.customization?.ledColor ? Array.from({ length: NUM_LEDS }, (_, i) => i < Math.round(percent * 16) ? this.customization.ledColor : null) : analogLeds(percent, state));
  }

  screenFill() {
    const level = this.plate?.screen?.level || 0.82;
    const color = this.screenColor || { r: 0x33, g: 0x7f, b: 0xd4 };
    const channel = (value) => Math.min(255, Math.round(value / level));
    return `rgb(${channel(color.r)}, ${channel(color.g)}, ${channel(color.b)})`;
  }

  digitalLeds() {
    if (this.customization?.ledColor) return Array.from({ length: NUM_LEDS }, () => this.customization.ledColor);
    if (!this.screenColor) return digitalQuadLeds();
    const max = Math.max(this.screenColor.r, this.screenColor.g, this.screenColor.b, 1);
    const scale = 220 / max;
    const led = [this.screenColor.r, this.screenColor.g, this.screenColor.b].map((value) => Math.min(1, (value * scale) / 255));
    if ((this.activeSensors || 4) !== 1) return Array.from({ length: NUM_LEDS }, () => led);
    const percent = Math.min(1, (this.digitalWater || 181) / TEMP_FULL_SCALE_F);
    const lit = Math.min(NUM_LEDS, Math.max(0, Math.round((Math.abs(percent * NEEDLE_SWEEP_DEG) / 360) * NUM_LEDS)));
    return Array.from({ length: NUM_LEDS }, (_, index) => (index < lit ? led : null));
  }

  drawDigital() {
    const elapsed = this.demo?.quad ? (performance.now() - this.demo.start) / 1000 : 0;
    const sourceReadings = quadReadings(this.customization?.paused ? (this.customization.pausedAt || 0) : elapsed);
    const sensors = this.customization?.sensors || [0, 1, 2, 3];
    const readings = sensors.map((index) => sourceReadings[index]);
    const count = this.activeSensors || 4;
    const pose = this.eggy && this.eggyVisitOrigin ? eggyPose(this.eggyVisitOrigin, this.eggyVisitStart, this.customization?.paused ? (this.customization.pausedTime || this.eggyVisitStart) : performance.now()) : null;
    if (this.eggy && (!this.eggyBg || !pose)) return;
    const fill = this.customization?.background || this.screenFill();
    const key = pose
      ? `eggy|${pose.x}|${pose.frame}|${pose.right ? 1 : 0}|${count}|${readings.slice(0, count).join("|")}`
      : `${fill}|${this.customization?.textColor}|${sensors.join(",")}|${count}|${readings.slice(0, count).join("|")}`;
    if (key === this.faceKey) return;
    this.faceKey = key;
    this.digitalWater = parseFloat(sourceReadings[0]);

    const ctx = this.digitalCtx;
    ctx.setTransform(DIGITAL_SUPERSAMPLE, 0, 0, DIGITAL_SUPERSAMPLE, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, SCREEN_SRC, SCREEN_SRC);
    if (pose) ctx.drawImage(this.eggyBg, 0, 0, SCREEN_SRC, SCREEN_SRC);
    else if (this.customization?.digitalBackground) ctx.drawImage(this.customization.digitalBackground, 0, 0, SCREEN_SRC, SCREEN_SRC);
    else {
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, SCREEN_SRC, SCREEN_SRC);
    }
    const layout = DIGITAL_LAYOUTS[count].map((slot, index) => ({ ...slot, label: SENSOR_LABELS[sensors[index]] }));
    ctx.fillStyle = this.customization?.textColor || "#fff";
    if (count === 1) {
      const family = pose && this.blennyReady ? BLENNY_FAMILY : RIFT_FAMILY;
      drawInkTop(ctx, readings[0], 120, SINGLE_VALUE_TOP, SINGLE_VALUE_SIZE, family, this.customization?.textColor);
      drawInkTop(ctx, layout[0].label, 120, SINGLE_LABEL_TOP, SINGLE_LABEL_SIZE, RIFT_FAMILY, this.customization?.textColor);
    } else layout.forEach((slot, index) => {
      drawOutline(ctx, readings[index], slot.x, slot.y, slot.size, this.customization?.textColor);
      drawOutline(ctx, slot.label, slot.labelX, slot.labelY, 16, this.customization?.textColor);
    });
    if (pose) ctx.drawImage(this.eggyFrames[(pose.right ? 0 : EGGY_FRAME_COUNT) + pose.frame], pose.x, EGGY_SPRITE_Y, EGGY_FRAME_W, EGGY_FRAME_H);

    const screen = this.faceCtx;
    screen.imageSmoothingEnabled = true;
    screen.imageSmoothingQuality = "high";
    screen.clearRect(0, 0, SCREEN_PX, SCREEN_PX);
    screen.drawImage(this.digital, 0, 0, SCREEN_PX, SCREEN_PX);
  }

  drawScreen(percent, state) {
    const angle = Math.round(percent * NEEDLE_SWEEP_DEG * 10) / 10;
    const key = `${angle}|${state}`;
    if (key === this.faceKey) return;
    this.faceKey = key;
    const ctx = this.faceCtx;
    const s = SCREEN_PX;
    const { face, needle, warning } = this.face;
    const custom = this.customization || {};
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, s, s);
    ctx.drawImage(custom.face || (state === "high" && warning ? warning : face), 0, 0, s, s);
    ctx.save();
    ctx.translate(s / 2, s / 2);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.drawImage(custom.needle || needle, -s / 2, -s / 2, s, s);
    ctx.restore();
  }

  drawLeds(colors) {
    const key = colors.map((c) => (c ? c.join("") : "-")).join("");
    if (key === this.ledKey) return;
    this.ledKey = key;
    const ctx = this.ledCtx;
    // Glows are summed after tone mapping, so overlapping halos run a little hot without this.
    const gain = parseFloat(this.getAttribute("led-gain") ?? "0.85");
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, this.plate.width, this.plate.height);
    ctx.globalCompositeOperation = "lighter";
    for (const led of this.plate.leds) {
      const color = colors[led.index];
      if (!color) continue;
      for (let k = 0; k < 3; k++) {
        if (!color[k]) continue;
        ctx.globalAlpha = Math.min(1, color[k] * gain);
        ctx.drawImage(this.atlas, led.sx + k * led.aw, led.sy, led.aw, led.ah, led.x, led.y, led.w, led.h);
      }
    }
  }
}

customElements.define("flock-gauge", FlockGauge);
